import { useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import Logout from './Logout';

interface InactivityTimerReturn {
  showWarning: boolean;
  extendSession: () => void;
}

const WARNING_MINUTES = 2;

const InactivityTimer = (timeoutMinutes: number = 15): InactivityTimerReturn => {
  const { authState } = useOktaAuth();
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const isAuthenticated = useRef<boolean>(false);
  const showWarningRef = useRef<boolean>(false);
  const { logout } = Logout();

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const clearAllTimers = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
      warningTimeoutId.current = null;
    }
    setShowWarning(false);
  };

  const handleLogout = () => {
    try {
      logout();
    } catch (error) {
      console.error('Logout failed. ', error);
    } finally {
      clearAllTimers();
    }
  };

  const resetTimer = (clearWarning: boolean = false): void => {
    // Don't reset timer if warning is already showing or user is not authenticated
    if ((showWarningRef.current && !clearWarning) || !isAuthenticated.current) {
      return;
    }

    if (clearWarning) {
      setShowWarning(false);
    }

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
    }

    const warningMs = (timeoutMinutes - WARNING_MINUTES) * 60 * 1000;
    const logoutMs = timeoutMinutes * 60 * 1000;

    // Show warning if warning minutes met
    warningTimeoutId.current = setTimeout(() => {
      setShowWarning(true);
    }, warningMs);

    // Logout after timeout minutes met
    timeoutId.current = setTimeout(() => {
      handleLogout();
    }, logoutMs);
  };

  const extendSession = (): void => {
    if (!authState?.isAuthenticated) {
      return;
    }
    resetTimer(true);
  };

  useEffect(() => {
    isAuthenticated.current = authState?.isAuthenticated || false;
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    // Clear timers if user is not authenticated
    if (!authState?.isAuthenticated) {
      clearAllTimers();
      return;
    }

    const events: (keyof DocumentEventMap)[] = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const activityDetected: EventListener = (): void => {
      resetTimer(false);
    };

    events.forEach((event) => {
      document.addEventListener(event, activityDetected);
    });

    resetTimer();

    // cleanup
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      if (warningTimeoutId.current) {
        clearTimeout(warningTimeoutId.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, activityDetected);
      });
    };
  }, [authState?.isAuthenticated]);

  return { showWarning, extendSession };
};

export default InactivityTimer;
